<template>
    <span :class="{ 'truncate': truncate }">
        <template v-if="column === 'name'">
            <nio-optional :value="fullName" />
            <nio-icon
                class="ml-2"
                name="regular/notes-paper-text"
                v-tooltip="appointment.contact.notes"
                v-if="appointment.contact.notes"
            />
        </template>
        <template v-if="column === 'time'">
            {{ datetime }}
            <nio-icon
                class="ml-2"
                name="regular/notes-paper-text"
                v-tooltip="appointment.notes"
                v-if="appointment.notes"
            />
        </template>
        <template v-if="column === 'phone'">
            <contact-phones
                :contact="appointment.contact"
                :contact-phone-id="appointment.contact_phone_id"
            />
        </template>
        <template v-if="column === 'email'">
            <nio-optional :value="appointment.contact.email" />
        </template>
        <template v-if="column === 'sentStatus'">
            <div class="inline-flex items-center space-x-3">
                <sms-status
                    :contact="appointment.contact"
                    :sent="appointment.sent_sms"
                />
                <email-status
                    :contact="appointment.contact"
                    :sent="appointment.sent_email"
                />
                <div v-if="isExcluded">
                    <nio-icon
                        class="text-yellow-500 text-base"
                        name="bold/alert-triangle"
                        v-tooltip="$t('appointments.appointmentsList.appointmentExcluded')"
                    />
                </div>
            </div>
        </template>
        <template v-if="column === 'status'">
            <nio-icon
                class="text-lg"
                :class="[statusClassName, { 'cursor-pointer': goToConversationEnabled }]"
                :name="statusIcon"
                @click="goToConversation"
                v-tooltip="{ content: appointmentStatus + formattedReply, allowHTML: true }"
            />
        </template>
        <template v-if="column === 'actions'">
            <nio-dropdown>
                <template #trigger>
                    <button class="link-gray text-base">
                        <nio-icon name="bold/navigation-menu-horizontal" />
                    </button>
                </template>

                <nio-dropdown-item
                    icon="regular/pencil-write"
                    @click="$emit('open-modal')"
                >
                    {{ $t('global.general.edit') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    icon="regular/messages-bubble-search"
                    @click="goToConversation"
                    v-if="goToConversationEnabled"
                >
                    {{ $t('contacts.contactsList.goToConversation') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    divided-top
                    icon="regular/messages-bubble-typing-1"
                    @click="$emit('action-click', 'sendSmsConfirmation')"
                    v-if="appointment.contact.phones.length && !appointment.sent_sms && !appointment.status"
                >
                    {{ $t('appointments.appointmentsList.sendSmsConfirmation') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    :divided-top="!appointment.contact.phones.length || appointment.sent_sms"
                    icon="regular/send-email-envelope"
                    @click="$emit('action-click', 'sendEmailConfirmation')"
                    v-if="appointment.contact.email && !appointment.sent_email && !appointment.status"
                >
                    {{ $t('appointments.appointmentsList.sendEmailConfirmation') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    divided-top
                    icon="regular/calendar-check"
                    @click="$emit('action-click', 'markConfirmed')"
                >
                    {{ $t('appointments.appointmentsList.markConfirmed') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    icon="regular/calendar"
                    @click="$emit('action-click', 'markUnknown')"
                >
                    {{ $t('appointments.appointmentsList.markNotConfirmed') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    icon="regular/calendar-disable"
                    @click="$emit('action-click', 'markCanceled')"
                >
                    {{ $t('appointments.appointmentsList.markCanceled') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    class="hover:text-red-500"
                    divided-top
                    icon="regular/bin-2-alternate"
                    @click="$emit('action-click', 'delete')"
                >
                    {{ $t('appointments.appointmentsList.delete') }}
                </nio-dropdown-item>
            </nio-dropdown>
        </template>
    </span>
</template>

<script>
    import { mapState } from 'vuex';
    import { shortnameToOutput } from '@/utils/emoji.js';
    import ContactPhones from '@/components/ContactPhones.vue';
    import EmailStatus from '@/components/EmailStatus.vue';
    import SmsStatus from '@/components/SmsStatus.vue';

    export default {
        components: {
            ContactPhones,
            EmailStatus,
            SmsStatus,
        },

        props: {
            column: {
                type: String,
                required: true,
            },
            appointment: {
                type: Object,
                required: true,
            },
            earliestExcludeAfterTime: {
                type: String,
                required: true,
            },
            latestExcludeBeforeTime: {
                type: String,
                required: true,
            },
            excludedMakes: {
                type: Array,
                required: true,
            },
            truncate: {
                type: Boolean,
                default: true,
            },
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            fullName() {
                return this.formatFullName(this.appointment.contact);
            },

            datetime() {
                return this.formatTime(this.appointment.datetime);
            },

            isExcluded() {
                const appointmentTime = moment(this.datetime, 'HH:mm:ss');
                const earliestExcludeAfterTime = moment(this.earliestExcludeAfterTime, 'HH:mm:ss');
                const latestExcludeBeforeTime = moment(this.latestExcludeBeforeTime, 'HH:mm:ss');
                const makeExcluded = this.excludedMakes.some(make => {
                    return this.appointment.related_vehicle_make && this.appointment.related_vehicle_make.toUpperCase().includes(make);
                });

                return makeExcluded ||
                    appointmentTime.isAfter(earliestExcludeAfterTime) ||
                    appointmentTime.isBefore(latestExcludeBeforeTime);
            },

            conversationId() {
                if (this.appointment.messages &&
                    this.appointment.messages[0] &&
                    this.appointment.messages[0].conversation.users &&
                    this.appointment.messages[0].conversation.users[0].id == this.authUser.id
                ) {
                    return this.appointment.messages[0].conversation_id;
                }

                return false;
            },

            statusIcon() {
                switch (this.appointment.status) {
                    case 'confirmed':
                        return 'bold/check-circle-1';
                    case 'canceled':
                        return 'bold/remove-circle';
                    case 'unknown':
                        return 'bold/alert-diamond';
                    default:
                        return 'bold/subtract-circle';
                }
            },

            statusClassName() {
                switch (this.appointment.status) {
                    case 'confirmed':
                        return 'text-green-500';
                    case 'canceled':
                        return 'text-red-500';
                    case 'unknown':
                        return 'text-yellow-500';
                    default:
                        return 'text-gray-400';
                }
            },

            appointmentStatus() {
                let responseMethodText = ' ';

                if (this.appointment.status_updated_by === 'sms') {
                    responseMethodText += `${this.$t('chat.messageList.bySms')}`;
                }

                if (this.appointment.status_updated_by === 'email') {
                    responseMethodText += this.$t('chat.messageList.byEmail');
                }
                switch (this.appointment.status) {
                    case 'confirmed':
                        return this.$t('chat.messageList.statusConfirmed') + responseMethodText;
                    case 'canceled':
                        return this.$t('chat.messageList.statusCanceled') + responseMethodText;
                    case 'unknown':
                        return this.$t('chat.messageList.statusUnknown');
                    default:
                        return '';
                }
            },

            formattedReply() {
                if (!this.appointment.reply || this.appointment.status_updated_by !== 'sms') {
                    return '';
                }

                return this.appointmentStatus ? `</br>"${shortnameToOutput(this.appointment.reply)}"` : `"${shortnameToOutput(this.appointment.reply)}"`;
            },

            goToConversationEnabled() {
                return this.appointment.sent_sms && this.appointment.reply && this.conversationId;
            },
        },

        methods: {
            goToConversation() {
                if (!this.goToConversationEnabled) {
                    return;
                }

                this.$router.push({
                    name: 'chat',
                    params: {
                        id: this.conversationId,
                    },
                });
            },
        },
    };
</script>
